import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Eye, Save, X } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import GrapesJS from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';
import 'grapesjs-preset-newsletter';
import { supabase } from '../../utils/supabase/client';
import { useEmailTemplate,useUpdateEmailTemplate } from '../../api/emails';
import './EmailEdit.css';
import { useAuth } from '../../api/AuthProvider';

export default function EmailEdit() {
  const { templateKey, language } = useParams();
  const navigate = useNavigate();
  const {profile} = useAuth(); 
  const { data: template, isLoading, error } = useEmailTemplate(profile.restaurantid, templateKey, language);
  const { mutateAsync: updateEmailTemplate, isLoading: isSaving } = useUpdateEmailTemplate();
  const [editedSubject, setEditedSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const updateTimeoutRef = useRef(null);

  // Initialize subject and body when template data loads
  useEffect(() => {
    if (template) {
      setEditedSubject(template.subject || '');
      setEmailBody(template.body || '');
    } else if (!isLoading && !error) {
      setEditedSubject('');
      setEmailBody('');
    }
  }, [template, isLoading, error]);

  // Update editor content when emailBody changes
  useEffect(() => {
    if (editorRef.current && emailBody && !isLoading) {
      editorRef.current.setComponents(emailBody);
    }
  }, [emailBody, isLoading]);

  // Upload image to Supabase
  const uploadImage = async (file) => {
    try {
      setIsUploading(true);

      if (!file || !file.type || !file.type.startsWith('image/')) {
        throw new Error('Избрани фајл не е слика.');
      }

      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        throw new Error('Сликата е премногу голема. Максимална големина е 5MB.');
      }

      const fileExt = file.name.split('.').pop().toLowerCase();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;


      const { data, error } = await supabase.storage
        .from('emailimages')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        console.error('Upload error:', error);
        throw error;
      }


      const { data: urlData } = supabase.storage
        .from('emailimages')
        .getPublicUrl(fileName);

      let publicUrl = urlData.publicUrl;
      if (publicUrl) {
        publicUrl = publicUrl.replace(/\\+/g, '').replace(/\/+$/, '');
      }


      return publicUrl;
    } catch (error) {
      console.error('Грешка при прикачување на слика:', error);

      let errorMessage = 'Грешка при прикачување на слика. Обидете се повторно.';
      if (error.message.includes('row-level security')) {
        errorMessage = 'Немате дозвола за прикачување слики. Контактирајте го администраторот.';
      } else if (error.message.includes('not found')) {
        errorMessage = 'Bucket-от не е пронајден. Контактирајте го администраторот.';
      } else if (error.message.includes('size')) {
        errorMessage = error.message;
      }

      alert(errorMessage);
      return null;
    } finally {
      setIsUploading(false);
    }
  };

    const convertToInlineStyles = (html, css) => {
    if (!css) return html;

    try {
      // Create a temporary DOM element to work with
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      // Parse CSS rules
      const styleElement = document.createElement('style');
      styleElement.textContent = css;
      document.head.appendChild(styleElement);

      const sheet = styleElement.sheet;
      if (sheet && (sheet.cssRules || sheet.rules)) {
        const rules = sheet.cssRules || sheet.rules;

        // Apply CSS rules as inline styles
        for (let i = 0; i < rules.length; i++) {
          const rule = rules[i];
          if (rule.type === CSSRule.STYLE_RULE) {
            const selector = rule.selectorText;
            try {
              const elements = tempDiv.querySelectorAll(selector);

              elements.forEach(element => {
                const existingStyle = element.getAttribute('style') || '';
                const ruleStyles = [];

                // Extract individual CSS properties from the rule
                for (let j = 0; j < rule.style.length; j++) {
                  const property = rule.style[j];
                  const value = rule.style.getPropertyValue(property);
                  const priority = rule.style.getPropertyPriority(property);

                  ruleStyles.push(`${property}: ${value}${priority ? ' !important' : ''}`);
                }

                // Merge styles, avoiding duplicates
                const existingProps = existingStyle.split(';')
                  .map(s => s.trim())
                  .filter(s => s)
                  .reduce((acc, style) => {
                    const [prop] = style.split(':');
                    if (prop) acc[prop.trim()] = style;
                    return acc;
                  }, {});

                ruleStyles.forEach(style => {
                  const [prop] = style.split(':');
                  if (prop) existingProps[prop.trim()] = style;
                });

                const combinedStyle = Object.values(existingProps).join('; ');
                if (combinedStyle) {
                  element.setAttribute('style', combinedStyle);
                }
              });
            } catch (selectorError) {
              console.warn('Invalid selector:', selector, selectorError);
            }
          }
        }
      }

      document.head.removeChild(styleElement);
      return tempDiv.innerHTML;
    } catch (error) {
      console.warn('Error applying inline styles:', error);
      return html;
    }
  };

  useEffect(() => {
    if (!editorRef.current && containerRef.current && !isLoading) {
      editorRef.current = GrapesJS.init({
        container: containerRef.current,
        fromElement: false,
        width: '100%',
        height: '600px',
        plugins: ['gjs-preset-newsletter'],
        storageManager: false,
        canvas: {
          styles: [
            'https://stackpath.bootstrapcdn.com/bootstrap/4.1.3/css/bootstrap.min.css'
          ]
        },
        assetManager: {
          upload: true,
          autoAdd: 1,
          assets: [],
          uploadText: 'Довлечете слики тука или кликнете за да изберете',
          addBtnText: 'Додај слика',
          uploadFile: async (e) => {
            try {
              const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
              if (!files || files.length === 0) {
                return [];
              }

              const results = [];

              for (const file of files) {
                const publicUrl = await uploadImage(file);
                if (publicUrl) {
                  const asset = {
                    src: publicUrl,
                    name: file.name,
                    type: 'image',
                    height: 'auto',
                    width: 'auto'
                  };

                  const addedAsset = editorRef.current.AssetManager.add(asset);
                  results.push(addedAsset);
                }
              }

              return results;
            } catch (error) {
              console.error('Error in uploadFile:', error);
              return [];
            }
          }
        },
        blockManager: {
          blocks: [
            {
              id: 'text-block',
              label: 'Текст',
              category: 'Основни',
              content: '<div style="padding: 10px;">Внесете го вашиот текст тука</div>',
            },
            {
              id: 'image-block',
              label: 'Слика',
              category: 'Основни',
              content: '<img src="https://via.placeholder.com/300x200" style="display: block; margin: 0 auto; width: 100%; max-width: 300px; height: auto;" alt="Слика"/>',            },
            {
              id: 'heading-block',
              label: 'Наслов',
              category: 'Основни',
              content: '<h2 style="padding: 10px; margin: 0;">Вашиот наслов тука</h2>',
            },
            {
              id: 'button-block',
              label: 'Копче',
              category: 'Основни',
              content: '<button type="button" style="display: inline-block; padding: 10px 20px; background-color: #007bff; color: white; border: none; border-radius: 5px; margin: 10px;">Копче</button>',
            },
            {
              id: 'list-block',
              label: 'Листа',
              category: 'Основни',
              content: '<ul style="padding: 10px;"><li>Ставка 1</li><li>Ставка 2</li><li>Ставка 3</li></ul>',
            },
            {
              id: 'divider-block',
              label: 'Разделувач',
              category: 'Основни',
              content: '<hr style="border: none; border-top: 1px solid #dee2e6; margin: 20px 0;">',
            },
            {
              id: 'container-block',
              label: 'Контејнер',
              category: 'Основни',
              content: '<div style="padding: 20px; border: 1px dashed #dee2e6; min-height: 50px;">Контејнер за содржина</div>',
            },
            {
              id: 'spacer-block',
              label: 'Простор',
              category: 'Основни',
              content: '<div style="height: 30px;"></div>',
            },
          ],
        },
        styleManager: {
          sectors: [
            {
              name: 'Типографија',
              open: true,
              properties: [
                {
                  name: 'Големина на фонт',
                  property: 'font-size',
                  type: 'integer',
                  units: ['px', 'em', 'rem'],
                  defaults: '16px',
                },
                {
                  name: 'Боја на текст',
                  property: 'color',
                  type: 'color',
                  defaults: '#333333',
                },
                {
                  name: 'Дебелина на фонт',
                  property: 'font-weight',
                  type: 'select',
                  defaults: 'normal',
                  list: [
                    { value: 'normal', name: 'Нормално' },
                    { value: 'bold', name: 'Задебелено' },
                    { value: '300', name: 'Тенко' },
                    { value: '600', name: 'Полуник' },
                  ],
                },
                {
                  name: 'Подредување на текст',
                  property: 'text-align',
                  type: 'radio',
                  defaults: 'left',
                  list: [
                    { value: 'left', name: 'Лево' },
                    { value: 'center', name: 'Центар' },
                    { value: 'right', name: 'Десно' },
                    { value: 'justify', name: 'Оправдано' },
                  ],
                },
                {
                  name: 'Висна на лини',
                  property: 'line-height',
                  type: 'integer',
                  units: ['px', 'em', '%'],
                  defaults: '1.4',
                },
              ],
            },
            {
              name: 'Простор',
              open: false,
              properties: [
                {
                  name: 'Внатрешенник',
                  property: 'padding',
                  type: 'composite',
                  properties: [
                    { name: 'Горе', property: 'padding-top', type: 'integer', units: ['px', 'em', '%'] },
                    { name: 'Десно', property: 'padding-right', type: 'integer', units: ['px', 'em', '%'] },
                    { name: 'Доле', property: 'padding-bottom', type: 'integer', units: ['px', 'em', '%'] },
                    { name: 'Лево', property: 'padding-left', type: 'integer', units: ['px', 'em', '%'] },
                  ],
                },
                {
                  name: 'Надворешен простор',
                  property: 'margin',
                  type: 'composite',
                  properties: [
                    { name: 'Горе', property: 'margin-top', type: 'integer', units: ['px', 'em', '%'] },
                    { name: 'Десно', property: 'margin-right', type: 'integer', units: ['px', 'em', '%'] },
                    { name: 'Доле', property: 'margin-bottom', type: 'integer', units: ['px', 'em', '%'] },
                    { name: 'Лево', property: 'margin-left', type: 'integer', units: ['px', 'em', '%'] },
                  ],
                },
              ],
            },
            {
              name: 'Позадина',
              open: false,
              properties: [
                {
                  name: 'Боја на пзадина',
                  property: 'background-color',
                  type: 'color',
                  defaults: 'transparent',
                },
              ],
            },
            {
              name: 'Граница',
              open: false,
              properties: [
                {
                  name: 'Ширина на граница',
                  property: 'border-width',
                  type: 'integer',
                  units: ['px'],
                  defaults: '0px',
                },
                {
                  name: 'Стил на граница',
                  property: 'border-style',
                  type: 'select',
                  defaults: 'solid',
                  list: [
                    { value: 'none', name: 'Без' },
                    { value: 'solid', name: 'Цврста' },
                    { value: 'dashed', name: 'Испрекинта' },
                    { value: 'dotted', name: 'Точкаста' },
                  ],
                },
                {
                  name: 'Боја на граница',
                  property: 'border-color',
                  type: 'color',
                  defaults: '#dee2e6',
                },
              ],
            },
          ],
        },
        layerManager: {
          appendTo: '.layers-container'
        },
        deviceManager: {
          devices: [
            {
              name: 'Десктоп',
              width: '',
            },
            {
              name: 'Мобилен',
              width: '320px',
              widthMedia: '480px',
            }
          ]
        }
      });

      if (emailBody && !isLoading) {
        editorRef.current.setComponents(emailBody);
      }

      const handleContentUpdate = () => {
        clearTimeout(updateTimeoutRef.current);
        updateTimeoutRef.current = setTimeout(() => {
          try {
            const html = editorRef.current.getHtml();
            const css = editorRef.current.getCss();
            const fullHtml = css ? `<style>${css}</style>${html}` : html;
            setEmailBody(fullHtml);
          } catch (error) {
            console.error('Error updating content:', error);
          }
        }, 100);
      };

      editorRef.current.on('component:update', handleContentUpdate);
      editorRef.current.on('style:update', handleContentUpdate);

      editorRef.current.on('asset:select', (asset) => {

        const selected = editorRef.current.getSelected();
        let cleanSrc = asset.get('src').replace(/\\+/g, '').replace(/\/+$/, '');

        if (selected && (selected.get('type') === 'image' || selected.get('tagName') === 'img')) {
          selected.addAttributes({ src: cleanSrc });
          selected.set({ src: cleanSrc });
          selected.trigger('change:attributes');
          selected.view.render();

        } else {
          const components = editorRef.current.getWrapper();
          const newImage = components.append({
            type: 'image',
            attributes: {
              src: cleanSrc,
              alt: asset.get('name') || 'Image',
              style: 'width: 100%; height: auto;'
            }
          })[0];

          editorRef.current.select(newImage);
        }

        const modal = editorRef.current.Modal;
        if (modal.isOpen()) {
          modal.close();
        }
      });

      editorRef.current.on('asset:add', (asset) => {
      });

      editorRef.current.on('asset:upload:error', (error) => {
        console.error('Asset upload error:', error);
        alert('Грешка при прикачуване на слика. Проверете ја конзолата за повеќе детали.');
      });

      editorRef.current.on('asset:upload:response', (response) => {
      });

      const panels = editorRef.current.Panels;
      panels.removeButton('options', 'sw-visibility');
      panels.removeButton('options', 'export-template');

      panels.addButton('options', {
        id: 'open-assets',
        className: 'gjs-pn-btn',
        label: '<i class="fa fa-images"></i>',
        command: 'core:open-assets',
        attributes: { title: 'Управуване' }
      });

      editorRef.current.Commands.add('asset-insert-image', {
        run: (editor, sender, options) => {
          const asset = options.asset;
          if (!asset) return;

          let cleanSrc = asset.get('src').replace(/\\+/g, '').replace(/\/+$/, '');

          const selected = editor.getSelected();
          if (selected && (selected.get('type') === 'image' || selected.get('tagName') === 'img')) {
            selected.addAttributes({ src: cleanSrc });
            selected.trigger('change:attributes');
          } else {
            const wrapper = editor.getWrapper();
            const newImage = wrapper.append({
              type: 'image',
              attributes: {
                src: cleanSrc,
                alt: asset.get('name') || 'Image',
                style: 'width: 100%; height: auto; display: block; margin: 10px auto;'
              }
            })[0];

            editor.select(newImage);
          }
        }
      });

      editorRef.current.on('asset:custom', (asset) => {
        editorRef.current.runCommand('asset-insert-image', { asset });
      });

      return () => {
        if (editorRef.current) {
          if (updateTimeoutRef.current) {
            clearTimeout(updateTimeoutRef.current);
          }

          try {
            editorRef.current.destroy();
          } catch (error) {
            console.error('Error destroying editor:', error);
          }
          editorRef.current = null;
        }
      };
    }
  }, [isLoading]);

  const handleSave = async () => {
  if (!editorRef.current || !profile.restaurantid) {
    alert('Editor or restaurant ID is missing.');
    return;
  }

  setIsUploading(true);

  try {
    let html = editorRef.current.getHtml();
    const css = editorRef.current.getCss();

    // Correctly access AssetManager.getAll()
    const assets = editorRef.current.AssetManager.getAll();
    const blobAssets = assets.filter(asset => {
      const src = asset.get('src');
      return src && src.startsWith('blob:');
    });

    const urlMap = new Map();

    await Promise.all(blobAssets.map(async (asset) => {
      const src = asset.get('src');
      const file = asset.get('file');
      if (file instanceof File) {
        const publicUrl = await uploadImage(file);
        if (publicUrl) {
          urlMap.set(src, publicUrl);
          asset.set('src', publicUrl);
        }
      }
    }));

    let updatedHtml = html;
    urlMap.forEach((supabaseUrl, blobUrl) => {
      const cleanSupabaseUrl = supabaseUrl.replace(/\\+/g, '').replace(/\/+$/, '');
      const escapedBlobUrl = blobUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      updatedHtml = updatedHtml.replace(new RegExp(escapedBlobUrl, 'g'), cleanSupabaseUrl);
    });

    const finalHtml = convertToInlineStyles(updatedHtml, css);

    await updateEmailTemplate({
      restaurantId:profile.restaurantid,
      templateKey,
      language,
      subject: editedSubject,
      body: finalHtml,
    });

    navigate('/email');
  } catch (error) {
    console.error('Error during save:', error);
    alert('Грешка при зачувуване. Обидете се повторно.');
  } finally {
    setIsUploading(false);
  }
};
  const handleCancel = () => {
    navigate('/email');
  };

  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  if (isLoading) {
    return <div>Се вчитува...</div>;
  }

  if (error) {
    return <div>Грешка при вчитувае на шаблон: {error.message}</div>;
  }

  return (
    <div className="email-edit-container">
      <div className="email-edit-header">
        <div className="email-edit-header-content">
          <button onClick={handleCancel} className="email-edit-back-button">
            <ArrowLeft className="email-edit-icon" />
            Назад
          </button>
          <h1>Уреди: {templateKey.charAt(0).toUpperCase() + templateKey.slice(1)} ({language})</h1>
        </div>
      </div>

      <div className="email-edit-content">
        <div className="email-edit-section">
          <label className="email-edit-form-label">Предмет</label>
          <input
            type="text"
            value={editedSubject}
            onChange={(e) => setEditedSubject(e.target.value)}
            className="email-edit-form-input"
            placeholder="Предмет на е-мејлот"
          />

          <label className="email-edit-form-label">Содржина</label>
          <div className="email-edit-grapesjs-container">
            <div ref={containerRef} className="email-edit-grapesjs-editor"></div>
            {(isUploading || isSaving) && (
              <div className="email-edit-upload-overlay">
                <div className="email-edit-upload-spinner">
                  <Save className="email-edit-icon" />
                  <span>Се зачувува...</span>
                </div>
              </div>
            )}
          </div>
          {showPreview && (
            <div className="email-edit-preview">
              <h3>Преглед</h3>
              <div
                className="email-edit-preview-pane"
                dangerouslySetInnerHTML={{ __html: emailBody }}
              />
            </div>
          )}
        </div>
        <div className="email-edit-footer">
          <button onClick={togglePreview} className="email-edit-preview-button">
            <Eye className="email-edit-icon" />
            {showPreview ? 'Сокриј' : 'Преглед'}
          </button>
          <button onClick={handleCancel} className="email-edit-cancel-button">
            <X className="email-edit-icon" />
            Откажи
          </button>
          <button onClick={handleSave} className="email-edit-save-button" disabled={isUploading || isSaving}>
            <Save className="email-edit-icon" />
            Зачувај
          </button>
        </div>
      </div>
    </div>
  );
}